import { DebugLog } from './DebugLog.js';

const SAVE_KEY = 'astralcrypts_save';
const META_KEY = 'astralcrypts_meta';

/**
 * Singleton holding the current run/game state and meta-progression.
 */
class GameStateSingleton {
  constructor() {
    // Run state
    this.floor = 1;
    this.playerHP = 100;
    this.playerMaxHP = 100;
    this.gold = 0;
    this.isRunActive = false;

    // Class selection (M11)
    this.selectedClass = 'ironclad';

    // Level system (M22)
    this.level = 1;
    this.xp = 0;
    this.xpToNext = 80; // 50 + 1*30
    this.statPoints = 0;
    this.statAllocations = { maxHp: 0, damage: 0, speed: 0, defense: 0 };

    // Relic system (M21)
    this.relics = []; // array of { relicId, relicData }

    // Run statistics (M7)
    this.enemiesDefeated = 0;
    this.goldCollected = 0;
    this.runStartTime = 0;

    // Item system (M9, expanded M13/M14)
    this.weapon = null;       // current weapon id (e.g. 'rusty_sword')
    this.inventory = [];      // array of { itemId, itemData } for any items (max 15)
    this.equipment = {        // equipped items (M13)
      weapon: null,           // { itemId, itemData }
      body: null,
      ring: null,
      amulet: null,
    };

    // Dungeon seed for save/restore (M10)
    this.dungeonSeed = 0;

    // Skill tree (M28)
    this.skillPoints = 0;
    this.skillAllocations = {}; // { branchId: tierLevel }

    // Run tracking for achievements (M28)
    this.dodgeCount = 0;
    this.bossDefeatedThisRun = false;
    this.noDamageBoss = false;

    // Meta-progression (M10) -- persists across runs
    this.cryptTokens = 0;
    this.totalRuns = 0;
    this.bestFloor = 0;
    this.totalEnemiesDefeated = 0;
    this.unlocks = [];
    this.achievements = [];
    this.runHistory = []; // last 10 runs
  }

  /** Returns true if a run save exists in localStorage. */
  get hasSave() {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return false;
      const data = JSON.parse(raw);
      return data.isRunActive === true;
    } catch {
      return false;
    }
  }

  /**
   * Returns how many seconds have elapsed since the run started.
   * @returns {number} Seconds elapsed
   */
  getRunDuration() {
    if (this.runStartTime === 0) return 0;
    return (Date.now() - this.runStartTime) / 1000;
  }

  /** Reset all run state for a fresh run. Uses selectedClass for HP/stats. */
  startNewRun() {
    this.floor = 1;
    this.gold = 0;
    this.isRunActive = true;

    // Class-aware HP defaults (actual values set by GameplayScene from class data)
    // We keep defaults here for save/load compatibility
    this.playerHP = 100;
    this.playerMaxHP = 100;

    // Reset run statistics
    this.enemiesDefeated = 0;
    this.goldCollected = 0;
    this.runStartTime = Date.now();

    // Reset item system
    this.weapon = null;
    this.inventory = [];
    this.equipment = { weapon: null, body: null, ring: null, amulet: null };

    // Reset level system (M22)
    this.level = 1;
    this.xp = 0;
    this.xpToNext = 80; // 50 + 1*30
    this.statPoints = 0;
    this.statAllocations = { maxHp: 0, damage: 0, speed: 0, defense: 0 };

    // Reset relics (M21)
    this.relics = [];

    // Reset skill tree (M28)
    this.skillPoints = 0;
    this.skillAllocations = {};
    this.dodgeCount = 0;
    this.bossDefeatedThisRun = false;
    this.noDamageBoss = false;

    // Generate a fresh dungeon seed
    this.dungeonSeed = Date.now();

    // Increment total runs
    this.totalRuns++;
    this.saveMeta();

    DebugLog.info('GameState', `New run started (class: ${this.selectedClass})`);
  }

  /**
   * Persist current run state to localStorage.
   * Includes full run data: floor, HP, gold, weapon, inventory, stats, dungeon seed, and selected class.
   */
  save() {
    try {
      const data = {
        floor: this.floor,
        playerHP: this.playerHP,
        playerMaxHP: this.playerMaxHP,
        gold: this.gold,
        weapon: this.weapon,
        inventory: this.inventory,
        equipment: this.equipment,
        enemiesDefeated: this.enemiesDefeated,
        goldCollected: this.goldCollected,
        runStartTime: this.runStartTime,
        isRunActive: this.isRunActive,
        dungeonSeed: this.dungeonSeed,
        selectedClass: this.selectedClass,
        level: this.level,
        xp: this.xp,
        xpToNext: this.xpToNext,
        statPoints: this.statPoints,
        statAllocations: this.statAllocations,
        relics: this.relics,
        skillPoints: this.skillPoints,
        skillAllocations: this.skillAllocations,
        dodgeCount: this.dodgeCount,
        bossDefeatedThisRun: this.bossDefeatedThisRun,
      };
      localStorage.setItem(SAVE_KEY, JSON.stringify(data));
      DebugLog.info('GameState', `Game saved (floor ${this.floor})`);
    } catch (e) {
      DebugLog.error('GameState', 'Save failed: ' + e.message);
    }
  }

  /**
   * Load run state from localStorage.
   * @returns {boolean} True if a save existed and was loaded successfully.
   */
  load() {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return false;
      const data = JSON.parse(raw);

      this.floor = data.floor ?? 1;
      this.playerHP = data.playerHP ?? 100;
      this.playerMaxHP = data.playerMaxHP ?? 100;
      this.gold = data.gold ?? 0;
      this.weapon = data.weapon ?? null;
      this.inventory = data.inventory ?? [];
      this.equipment = data.equipment ?? { weapon: null, body: null, ring: null, amulet: null };
      this.enemiesDefeated = data.enemiesDefeated ?? 0;
      this.goldCollected = data.goldCollected ?? 0;
      this.runStartTime = data.runStartTime ?? Date.now();
      this.isRunActive = data.isRunActive ?? false;
      this.dungeonSeed = data.dungeonSeed ?? Date.now();
      this.selectedClass = data.selectedClass ?? 'ironclad';
      this.level = data.level ?? 1;
      this.xp = data.xp ?? 0;
      this.xpToNext = data.xpToNext ?? 80;
      this.statPoints = data.statPoints ?? 0;
      this.statAllocations = data.statAllocations ?? { maxHp: 0, damage: 0, speed: 0, defense: 0 };
      this.relics = data.relics ?? [];
      this.skillPoints = data.skillPoints ?? 0;
      this.skillAllocations = data.skillAllocations ?? {};
      this.dodgeCount = data.dodgeCount ?? 0;
      this.bossDefeatedThisRun = data.bossDefeatedThisRun ?? false;

      DebugLog.info('GameState', `Game loaded (floor ${this.floor}, class: ${this.selectedClass})`);
      return true;
    } catch (e) {
      DebugLog.error('GameState', 'Load failed: ' + e.message);
      return false;
    }
  }

  /** Clear the run save (called on death). */
  clearRunSave() {
    try {
      localStorage.removeItem(SAVE_KEY);
      DebugLog.info('GameState', 'Run save cleared');
    } catch (e) {
      DebugLog.error('GameState', 'Failed to clear run save: ' + e.message);
    }
  }

  /**
   * Add crypt tokens (meta currency).
   * @param {number} amount - Tokens to add
   */
  addCryptTokens(amount) {
    this.cryptTokens += amount;
    DebugLog.info('GameState', `Earned ${amount} crypt tokens (total: ${this.cryptTokens})`);
  }

  /**
   * Record the end of a run. Updates lifetime stats and awards crypt tokens.
   * Should be called on player death or victory.
   */
  recordRunEnd() {
    // Update best floor
    if (this.floor > this.bestFloor) {
      this.bestFloor = this.floor;
    }

    // Update lifetime kills
    this.totalEnemiesDefeated += this.enemiesDefeated;

    // Award crypt tokens: floor * 5 + enemies/10 + boss bonus
    const floorTokens = this.floor * 5;
    const killTokens = Math.floor(this.enemiesDefeated / 10);
    const tokensEarned = floorTokens + killTokens;
    this.addCryptTokens(tokensEarned);

    this.isRunActive = false;

    // Record run history (M28 - keep last 10)
    this.runHistory.unshift({
      class: this.selectedClass,
      floor: this.floor,
      enemiesDefeated: this.enemiesDefeated,
      goldCollected: this.goldCollected,
      level: this.level,
      relics: (this.relics || []).length,
      time: Math.floor(this.getRunDuration()),
      tokensEarned,
      date: Date.now(),
    });
    if (this.runHistory.length > 10) this.runHistory.length = 10;

    DebugLog.info('GameState', `Run ended. Floor: ${this.floor}, Tokens earned: ${tokensEarned}`);

    return tokensEarned;
  }

  /**
   * Purchase an unlock tier in a category.
   * @param {string} categoryId - Unlock category key
   * @param {number} cost - Token cost for this tier
   * @returns {boolean} True if purchased successfully
   */
  purchaseUnlock(categoryId, cost) {
    if (this.cryptTokens < cost) return false;
    this.cryptTokens -= cost;
    this.unlocks.push(categoryId);
    this.saveMeta();
    DebugLog.info('GameState', `Purchased unlock: ${categoryId} (tier ${this.getUnlockTier(categoryId)})`);
    return true;
  }

  /**
   * Get the current tier level for a category (number of times purchased).
   * @param {string} categoryId
   * @returns {number}
   */
  getUnlockTier(categoryId) {
    return this.unlocks.filter(id => id === categoryId).length;
  }

  /**
   * Calculate total stat bonuses from all unlocks.
   * @param {object} unlocksData - The unlocks.json data
   * @returns {{ maxHp: number, damage: number, defense: number, goldMod: number }}
   */
  getUnlockBonuses(unlocksData) {
    const bonuses = { maxHp: 0, damage: 0, defense: 0, goldMod: 0 };
    const categories = unlocksData.categories;

    for (const [catId, cat] of Object.entries(categories)) {
      const tier = this.getUnlockTier(catId);
      for (let i = 0; i < tier && i < cat.tiers.length; i++) {
        const effect = cat.tiers[i].effect;
        if (effect.maxHp) bonuses.maxHp += effect.maxHp;
        if (effect.damage) bonuses.damage += effect.damage;
        if (effect.defense) bonuses.defense += effect.defense;
        if (effect.goldMod) bonuses.goldMod += effect.goldMod;
      }
    }

    return bonuses;
  }

  /**
   * Save meta-progression to localStorage (persists even when run save is deleted).
   */
  saveMeta() {
    try {
      const data = {
        cryptTokens: this.cryptTokens,
        totalRuns: this.totalRuns,
        bestFloor: this.bestFloor,
        totalEnemiesDefeated: this.totalEnemiesDefeated,
        unlocks: this.unlocks,
        achievements: this.achievements,
        runHistory: this.runHistory,
      };
      localStorage.setItem(META_KEY, JSON.stringify(data));
      DebugLog.info('GameState', 'Meta-progression saved');
    } catch (e) {
      DebugLog.error('GameState', 'Meta save failed: ' + e.message);
    }
  }

  /**
   * Load meta-progression from localStorage.
   * @returns {boolean} True if meta data existed and was loaded.
   */
  loadMeta() {
    try {
      const raw = localStorage.getItem(META_KEY);
      if (!raw) return false;
      const data = JSON.parse(raw);

      this.cryptTokens = data.cryptTokens ?? 0;
      this.totalRuns = data.totalRuns ?? 0;
      this.bestFloor = data.bestFloor ?? 0;
      this.totalEnemiesDefeated = data.totalEnemiesDefeated ?? 0;
      this.unlocks = data.unlocks ?? [];
      this.achievements = data.achievements ?? [];
      this.runHistory = data.runHistory ?? [];

      DebugLog.info('GameState', `Meta loaded: ${this.totalRuns} runs, best floor ${this.bestFloor}, ${this.cryptTokens} tokens`);
      return true;
    } catch (e) {
      DebugLog.error('GameState', 'Meta load failed: ' + e.message);
      return false;
    }
  }
}

export const GameState = new GameStateSingleton();
